import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { parseISO } from 'date-fns';
import {
  createDeskBlocks, createDeskBooking, deleteDeskBlocks, deleteDeskBooking, extractErrorMessage, getDeskBookings, moveDeskBooking,
  setDeskActive,
} from '../../api/desk-bookings';
import { isOwnerOrAdmin } from '../../helpers';
import { formatDate } from '../../helpers/dates';
import { getFirstBookableDate, indexBookings } from '../../helpers/desk-booking';
import BlockDesksBar from '../blocks/desk_booking/BlockDesksBar';
import DateNav from '../blocks/desk_booking/DateNav';
import DeskCalendar from '../blocks/desk_booking/DeskCalendar';
import DeskLegend from '../blocks/desk_booking/DeskLegend';
import FloorPlan from '../blocks/desk_booking/FloorPlan';
import MyDays from '../blocks/desk_booking/MyDays';
import MainNav from '../blocks/navigation/MainNav';
import Alert from '../elements/Alert';
import StaticLoader from '../elements/StaticLoader';
import UserSelect from '../elements/UserSelect';
import type { DeskBookingsPayload, User } from '../../types';

type Props = {
  currentUser: User,
  users: Array<User>,
};

const MOBILE_QUERY = '(max-width: 991.98px)';

type PanelTab = 'days' | 'desk';

const DeskBooking = (props: Props): React.ReactElement => {
  const { currentUser, users } = props;
  const navigate = useNavigate();
  const canManageOthers = isOwnerOrAdmin(currentUser);

  const [data, setData] = useState<DeskBookingsPayload | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [selectedDeskId, setSelectedDeskId] = useState<number | null>(null);
  const [tab, setTab] = useState<PanelTab>('days');
  const [pickDate, setPickDate] = useState<string | null>(null);
  const [checkedDeskIds, setCheckedDeskIds] = useState<Array<number> | null>(null);
  const [targetUser, setTargetUser] = useState<User>(currentUser);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    try {
      const payload = await getDeskBookings();
      setData(payload);
      setDate((current) => current || getFirstBookableDate(payload.range, payload.holidays));
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!pickDate) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPickDate(null);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pickDate]);

  const index = useMemo(() => indexBookings(data?.desks || [], data?.bookings || [], data?.blocks || []), [data]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      await fetchData();
      setBusy(false);
    }
  };

  const scrollOnMobile = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (window.matchMedia(MOBILE_QUERY).matches) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    }
  };

  const handleStartPick = (day: string) => {
    if (pickDate === day) {
      setPickDate(null);

      return;
    }
    setPickDate(day);
    setDate(day);
    setCheckedDeskIds(null);
    scrollOnMobile(mapRef);
  };

  const handlePickDesk = (deskId: number) => {
    if (!pickDate) return;

    const day = pickDate;
    setPickDate(null);
    run(() => createDeskBooking({ deskId, date: day, userId: targetUser.id }));
    scrollOnMobile(panelRef);
  };

  const handleDateChange = (day: string) => {
    setPickDate(null);
    setDate(day);
    setCheckedDeskIds((current) => current && []);
  };

  const handleToggleDesk = (deskId: number) => {
    setCheckedDeskIds((current) => current && (
      current.includes(deskId) ? current.filter((id) => id !== deskId) : [...current, deskId]
    ));
  };

  const handleTargetUserChange = (user: User) => {
    setPickDate(null);
    setTargetUser(user);
  };

  const handleSelectDesk = (deskId: number | null) => {
    setPickDate(null);
    setSelectedDeskId(deskId);
    setTab(deskId ? 'desk' : 'days');
    if (deskId) scrollOnMobile(panelRef);
  };

  const actingForSelf = targetUser.id === currentUser.id;
  const targetUserName = `${targetUser.name} ${targetUser.surname}`;
  const selectedDesk = selectedDeskId === null ? undefined : index.desksById[selectedDeskId];
  const bookableUsers = users.filter((user) => user.active && user.role !== 'client');

  return (
    <div className="container-view view-wrapper desk-booking-view">
      <MainNav currentUser={currentUser} navigate={navigate} />
      <div className="container container-header mt-4">
        <div className="upper-header-wrapper">
          <div className="left-column">
            <h2>Desk booking</h2>
            <hr className="float-left" />
          </div>
        </div>
      </div>

      <div className="container-fluid desk-booking mt-3">
        <Alert display={!!error} message={error || ''} type="danger" onClose={() => setError(null)} />

        {!data || !date ? (
          <StaticLoader display={!error} />
        ) : (
          <div className="row">
            <div className="col-lg-6 mb-4" ref={mapRef}>
              <div className="desk-booking__card">
                <DateNav
                  date={date}
                  today={data.today}
                  range={data.range}
                  onDateChange={handleDateChange}
                  actions={canManageOthers && !checkedDeskIds && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setPickDate(null);
                        setCheckedDeskIds([]);
                      }}
                    >
                      Block desks
                    </button>
                  )}
                />
                {checkedDeskIds && (
                  <BlockDesksBar
                    desks={data.desks}
                    date={date}
                    index={index}
                    checkedDeskIds={checkedDeskIds}
                    busy={busy}
                    onCheckedChange={setCheckedDeskIds}
                    onBlock={(deskIds, reason) => {
                      setCheckedDeskIds([]);
                      run(() => createDeskBlocks({ deskIds, date, reason }));
                    }}
                    onUnblock={(blocks) => {
                      setCheckedDeskIds([]);
                      run(() => deleteDeskBlocks(blocks.map((block) => block.id)));
                    }}
                    onDone={() => setCheckedDeskIds(null)}
                  />
                )}
                {pickDate && (
                  <div className="desk-pick-banner">
                    <span>
                      Picking a desk for <strong>{formatDate(parseISO(pickDate), 'EEEE, dd.MM.')}</strong>
                      {!actingForSelf && <> ({targetUserName})</>} — click a free desk on the map.
                    </span>
                    <button type="button" className="btn btn-sm btn-light" onClick={() => setPickDate(null)}>Cancel</button>
                  </div>
                )}
                <div className="desk-booking__map">
                  <FloorPlan
                    desks={data.desks}
                    date={date}
                    index={index}
                    targetUserId={targetUser.id}
                    selectedDeskId={selectedDeskId}
                    picking={!!pickDate}
                    checkedDeskIds={checkedDeskIds}
                    canSelectInactive={canManageOthers}
                    onSelectDesk={handleSelectDesk}
                    onPickDesk={handlePickDesk}
                    onToggleDesk={handleToggleDesk}
                  />
                </div>
                <DeskLegend
                  items={[
                    { swatch: 'free', label: 'Free' },
                    { swatch: 'mine', label: actingForSelf ? 'Yours' : targetUserName },
                    { swatch: 'taken', label: 'Taken' },
                    { swatch: 'blocked', label: 'Blocked' },
                    { swatch: 'inactive', label: 'Not bookable' },
                  ]}
                />
              </div>
            </div>

            <div className="col-lg-6 mb-4" ref={panelRef}>
              <div className="desk-booking__card">
                {canManageOthers && (
                  <div className="form-group desk-booking__for">
                    <label htmlFor="desk-booking-user">Booking for</label>
                    <UserSelect
                      inputId="desk-booking-user"
                      users={bookableUsers}
                      selectedUser={targetUser}
                      onSelectedUserChange={handleTargetUserChange}
                    />
                  </div>
                )}

                <div className="desk-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'days'}
                    className={`desk-tabs__tab${tab === 'days' ? ' desk-tabs__tab--active' : ''}`}
                    onClick={() => {
                      setPickDate(null);
                      setTab('days');
                    }}
                  >
                    {actingForSelf ? 'My days' : `${targetUser.name}'s days`}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'desk'}
                    className={`desk-tabs__tab${tab === 'desk' ? ' desk-tabs__tab--active' : ''}`}
                    disabled={!selectedDesk}
                    onClick={() => {
                      setPickDate(null);
                      setTab('desk');
                    }}
                  >
                    {selectedDesk ? `Desk ${selectedDesk.label}` : <>Desk <small>click a desk on the map</small></>}
                  </button>
                </div>

                {tab === 'desk' && selectedDesk ? (
                  <DeskCalendar
                    key={selectedDesk.id}
                    desk={selectedDesk}
                    index={index}
                    range={data.range}
                    holidays={data.holidays}
                    today={data.today}
                    targetUserId={targetUser.id}
                    targetUserName={targetUserName}
                    actingForSelf={actingForSelf}
                    canManageOthers={canManageOthers}
                    busy={busy}
                    onBook={(day) => run(() => createDeskBooking({ deskId: selectedDesk.id, date: day, userId: targetUser.id }))}
                    onCancel={(booking) => run(() => deleteDeskBooking(booking.id))}
                    onMove={(booking) => run(() => moveDeskBooking(booking.id, selectedDesk.id))}
                    onBlock={(day, reason) => run(() => createDeskBlocks({ deskIds: [selectedDesk.id], date: day, reason }))}
                    onUnblock={(block) => run(() => deleteDeskBlocks([block.id]))}
                    onSetActive={(active) => run(() => setDeskActive(selectedDesk.id, active))}
                    onClose={() => handleSelectDesk(null)}
                  />
                ) : (
                  <MyDays
                    index={index}
                    range={data.range}
                    holidays={data.holidays}
                    today={data.today}
                    mapDate={date}
                    pickDate={pickDate}
                    targetUserId={targetUser.id}
                    targetUserName={targetUserName}
                    actingForSelf={actingForSelf}
                    busy={busy}
                    onShowDate={handleDateChange}
                    onStartPick={handleStartPick}
                    onCancel={(booking) => run(() => deleteDeskBooking(booking.id))}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeskBooking;
